export interface AlfredSupportedPair {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  businessId: number | null;
  /** decimal-string, e.g. "10000.00" */
  maxQuantity: string;
  /** decimal-string, e.g. "1.00" */
  minQuantity: string;
  /** allowed decimal places in the from-amount; null = no constraint */
  decimals: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AlfredSupportedChain {
  id: string;
  currency: string;
  chain: string;
  offRampSupported: boolean;
  onRampSupported: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlfredSupportedFiatType {
  id: string;
  currency: string;
  fiatType: string;
  offRampSupported: boolean;
  onRampSupported: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AlfredAllConfigs {
  supportedPairs: AlfredSupportedPair[];
  supportedChains: AlfredSupportedChain[];
  supportedFiatTypes: AlfredSupportedFiatType[];
}

export interface AlfredQuoteFee {
  /** `commissionFee | processingFee | taxFee | networkFee` etc. */
  type: string;
  amount: string;
  currency: string;
}

export interface AlfredQuote {
  quoteId: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string;
  toAmount: string;
  chain: string | null;
  paymentMethodType: "BANK";
  expiration: string;
  fees: AlfredQuoteFee[];
  rate: string;
  metadata: Record<string, unknown>;
}

export interface QuotePreviewPayload {
  source_currency_id: string;
  target_currency_id: string;
  /** decimal-string ("1000" / "1000.00") — Alfred 422s on numeric */
  from_amount: string;
  /**
   * Optional but strongly recommended: lets the backend forward
   * `chain` to Alfred's /quotes so the live preview matches the
   * /onramp quote at submit. Without it Alfred returns a conservative
   * max-networkFee that disagrees with the real one (~30% haircut on
   * small amounts).
   */
  payment_route_id?: string;
}

/**
 * One row of the widget's currency picker. The user sees these in a
 * dropdown like "🇲🇽 MXN — SPEI"; selecting one drives the form's
 * min/max hint, decimal mask, and the value of `source_currency_id`
 * sent to `POST /psp/alfred/session`.
 */
export interface AlfredSupportedOption {
  /** ISO fiat the user pays in (deposit) or receives (withdrawal) */
  source_currency: string;
  /** Stable counterpart that's wallet-side (e.g. "USDC") */
  target_currency: string;
  /** Local rail name displayed alongside the currency: SPEI/PIX/COELSA/ACH */
  rail: string;
  /** Decimal-string, e.g. "50.00" — Alfred-min for this pair */
  min_amount: string;
  /** Decimal-string, e.g. "170799.99" — Alfred-max for this pair */
  max_amount: string;
  /** Allowed decimals in `fromAmount`; null = no constraint */
  decimals: string | null;
}

export interface AlfredSupportedOptionsResponse {
  payment_route_id: string;
  target_currency: string;
  direction: "onramp" | "offramp";
  options: AlfredSupportedOption[];
}

export interface InitiateDepositPayload {
  exchange: string;
  session_id: string;
  success_url: string;
  cancel_url: string;
  error_url: string;
  /**
   * Country-specific bank identifier the user picks in the form.
   * Today only Colombia (COP) requires this — Alfred 422s with
   * `errorCode 110002 "bankId is required in metadata for COP currency"`
   * without it. Format is a 4-digit PSE code from Colombia's
   * Superintendencia Financiera registry. Other rails ignore it.
   */
  bank_id?: string;
}

/**
 * Bank-deposit instructions Alfred returns inline on /onramp creation
 * for non-redirect rails (SPEI / PIX / COELSA / ACH …). Field set
 * varies per rail — `paymentType` is the only stable key. The widget
 * renders all received fields generically; the helper map in
 * `AlfredPaymentInstructions` adds nicer labels for known fields.
 */
export interface AlfredFiatPaymentInstructions {
  paymentType: string;
  expirationDate?: string;
  paymentDescription?: string;
  bankName?: string;
  accountHolderName?: string;
  clabe?: string;
  reference?: string | number;
  pixKey?: string;
  pixCode?: string;
  cbu?: string;
  cvu?: string;
  cci?: string;
  accountNumber?: string;
  routingNumber?: string;
  iban?: string;
  swiftBic?: string;
  beneficiaryAddress?: string;
  [key: string]: unknown;
}

export interface AlfredInitiateDepositTransaction {
  transactionId: string;
  status: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string;
  toAmount: string;
  chain: string;
  expirationDate?: string;
  fiatPaymentInstructions?: AlfredFiatPaymentInstructions;
  quote?: {
    quoteId: string;
    expiration: string;
    rate: string;
  };
}

export interface InitiateDepositResponse {
  status: "ok" | "error";
  /** Legacy hosted-onramp rails — Alfred SPEI/PIX/COELSA/ACH return
   *  payment instructions inline instead and `redirectUrl` is unset. */
  redirectUrl?: string;
  transaction?: AlfredInitiateDepositTransaction;
}

/**
 * Live session snapshot the widget polls while waiting for the user
 * to complete the bank transfer. Backend updates `transactionStatus`
 * on every Alfred webhook (real or QA-emulated).
 */
export interface AlfredSessionSnapshot {
  user_id?: string;
  customerId?: string;
  language?: string | null;
  transactionId?: string;
  transactionStatus?: string;
  paymentInstructions?: AlfredFiatPaymentInstructions | null;
  quoteExpiration?: string;
  lastWebhookAt?: number;
  /** Last non-FAILED status the session saw — set by the backend
   *  when a FAILED webhook lands so the widget can pin the X on the
   *  exact step where the chain broke. */
  failedAtStep?: string | null;
  /** Free-form reason from Alfred's FAILED payload. */
  failureReason?: string | null;
  [key: string]: unknown;
}

export interface AlfredDevStatusesResponse {
  kyc: string[];
  onramp: string[];
  offramp: string[];
}

/**
 * Body for `POST /psp/alfred/initiate-withdrawal`. The backend resolves
 * the cached session for amount/currency/customerId, mints a USDC→fiat
 * quote, and creates the offramp at Alfred. `fiat_account_id` is the
 * Alfred-side id of the user-picked beneficiary fiatAccount (see
 * {@link AlfredFiatAccount} / `listFiatAccounts`). As of 2026-05 Alfred
 * `/offramp` requires `fiatAccountId` and rejects the legacy
 * `paymentMethodId`.
 */
export interface InitiateWithdrawalPayload {
  session_id: string;
  fiat_account_id: string;
  /** USDT amount the user confirmed; backend rejects on session mismatch. */
  amount?: number;
}

export interface InitiateWithdrawalResponse {
  status: "ok";
  transaction: AlfredInitiateWithdrawalTransaction;
  quote: AlfredQuote;
}

export interface AlfredInitiateWithdrawalTransaction {
  transactionId: string;
  status: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string;
  toAmount: string;
  chain: string;
  /** The address Alfred wants the USDC sent to. */
  depositAddress: string;
  expirationDate?: string;
  fiatAccountId?: string;
  memo?: string;
  quote?: { quoteId: string; expiration: string; rate: string };
}

/**
 * One correctable text/boolean/date/file field Alfred wants the user to
 * re-supply on the `KYC_UPDATE_REQUIRED` fallback screen. `prefill` is the
 * last value Alfred has on file (when present) so the form can seed it.
 * `FILE_UPLOAD` fields also appear in {@link KycFallbackDocument} and must
 * be re-uploaded — the form renders a file input for those.
 */
export interface KycFallbackField {
  fieldName: string;
  type: "STRING" | "DATE" | "BOOLEAN" | "FILE_UPLOAD";
  required: boolean;
  prefill?: string;
}

/** A document Alfred requires re-uploaded on the fallback screen. */
export interface KycFallbackDocument {
  fieldName: string;
}

/** Parsed `fallback` payload from a `KYC_UPDATE_REQUIRED` eligibility result. */
export interface KycFallback {
  /** ISO-2 country from the descriptor — drives per-country placeholders/validation. */
  country?: string;
  fields: KycFallbackField[];
  documents: KycFallbackDocument[];
}

/**
 * Inputs the fallback form collects before building the multipart body.
 * `corrections` is fieldName→value for text/date/boolean fields; `documents`
 * is fieldName→File for each required upload.
 */
export interface SubmitKycFallbackPayload {
  corrections: Record<string, string>;
  documents: Record<string, File>;
}

export interface SubmitKycFallbackResponse {
  status: "submitted" | "error";
  message?: string;
}

/**
 * Result of `GET /psp/alfred/eligibility`. `{eligible:true}` clears the
 * gate. `{eligible:false, code}` blocks with one of the static codes (see
 * {@link AlfredIneligibleBlocker}). The `KYC_UPDATE_REQUIRED` case carries
 * a `fallback` schema so the widget can render the correction form instead.
 */
export type AlfredEligibility =
  | { eligible: true }
  | { eligible: false; code: string; fallback?: KycFallback };

export interface GetBanksParams {
  exchange: string;
}

export type AlfredPaymentMethodStatus =
  | "pending"
  | "active"
  | "rejected"
  | "archived";

/** Full Alfred Payment Method as the backend forwards it. */
export interface AlfredPaymentMethod {
  paymentMethodId: string;
  customerId: string;
  countryCode: string;
  currency: string;
  type: "BANK";
  status: AlfredPaymentMethodStatus;
  bankName: string;
  accountOwner: string;
  accountNumber: string;
  accountType?: string;
  routingNumber?: string;
  documentType?: string;
  documentNumber?: string;
  createdAt?: string;
}

export interface CreatePaymentMethodPayload {
  session_id: string;
  countryCode: string;
  currency: string;
  bankName: string;
  accountOwner: string;
  accountNumber: string;
  accountType?: string;
  routingNumber?: string;
  documentType?: string;
  documentNumber?: string;
}

/**
 * Alfred FiatAccount controller — newer offramp-recipient flow,
 * coexists alongside the legacy `payment-methods` controller. `type`
 * discriminates the rail; `fiatAccountFields` is rail-specific (e.g.
 * PIX wants `accountType=CPF` + `accountNumber=<CPF>`, SPEI wants
 * `accountType=CLABE` + 18-digit `accountNumber`). Per-rail schema is
 * surfaced via `getFiatAccountRequirements()` so dynamic forms render
 * the right inputs.
 */
export type AlfredFiatAccountType =
  | "SPEI"
  | "PIX"
  | "COELSA"
  | "ACH"
  | "ACH_DOM"
  | "BANK_CN"
  | "BANK_USA"
  | "ACH_CHL"
  | "ACH_BOL"
  | "B89";

export interface AlfredFiatAccountFields {
  accountNumber?: string;
  accountType?: string;
  accountName?: string;
  accountBankCode?: string;
  accountAlias?: string;
  networkIdentifier?: string;
  bankStreet?: string;
  bankCity?: string;
  bankState?: string;
  bankCountry?: string;
  bankPostalCode?: string;
  routingNumber?: string;
  [k: string]: unknown;
}

export interface AlfredFiatAccount {
  fiatAccountId: string;
  customerId: string;
  type: AlfredFiatAccountType;
  isExternal?: boolean;
  fiatAccountFields: AlfredFiatAccountFields;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateFiatAccountPayload {
  session_id: string;
  type: AlfredFiatAccountType;
  fiatAccountFields: AlfredFiatAccountFields;
  isExternal?: boolean;
}

/** Per-rail registration schema as exposed by Alfred — opaque to us. */
export type AlfredFiatAccountRequirements = Record<string, unknown>;

/**
 * Local mapping ISO country → Alfred fiatAccount `type`. Mirrors
 * vakotrade's `ALFRED_FIAT_COUNTRY_MAP` plus rail-specific knowledge:
 * Alfred uses different `type` enums for the same nominal "ACH" rail
 * depending on country (US=BANK_USA, CO=ACH, CL=ACH_CHL, BO=ACH_BOL).
 * Used by the Add-bank-account widget form to default `type` from the
 * fiat the user has selected; the user can still override.
 */
export const ALFRED_FIAT_ACCOUNT_TYPE_BY_COUNTRY: Readonly<Record<string, AlfredFiatAccountType>> = {
  BR: "PIX",
  MX: "SPEI",
  AR: "COELSA",
  CO: "ACH",
  US: "BANK_USA",
  CN: "BANK_CN",
  HK: "BANK_CN",
  CL: "ACH_CHL",
  BO: "ACH_BOL",
  PY: "B89",
  DO: "ACH_DOM",
};

export const ALFRED_FIAT_CURRENCY_TO_COUNTRY: Readonly<Record<string, string>> = {
  MXN: "MX",
  BRL: "BR",
  ARS: "AR",
  COP: "CO",
  DOP: "DO",
  USD: "US",
  CNY: "CN",
  BOB: "BO",
  CLP: "CL",
  PEN: "PE",
  PYG: "PY",
  HKD: "HK",
};

/**
 * Legacy `Bank` shape kept so existing withdrawal-step components
 * don't need restructuring. Maps to a real `AlfredPaymentMethod`:
 *
 *   bankGroupId         → paymentMethodId
 *   bankGroupName       → display label "BankName — ****1234"
 *   universalBranchCode → countryCode (ISO-3166-1, e.g. "MX")
 */
export interface Bank {
  bankGroupName: string;
  bankGroupId: string;
  universalBranchCode: string;
}
